import AppGrid from "@/features/order-sheet-sets/app-grid";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import useIdentity from "@/features/ui/hooks/use-identity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import UploadOrderSheetsModal from "@/features/order-sheet-sets/upload-order-sheets-modal";
import { Flex, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import { findParentGridRow } from "@/features/order-sheet-sets/helpers/app-grid-row-helper";
import useBuyerOrderSheetSetRows from "@/features/order-status/hooks/use-buyer-order-sheet-set-rows";
import OrderOrderSheetSetModal from "@/features/order-sheet-sets/order-order-sheet-set-modal";
import OrderSheetRowTooltipLabel from "@/features/order-sheet-sets/order-sheet-row-tooltip-label";
import {
  BuyerAttachmentRow,
  BuyerLineSheetRow,
} from "@/features/order-sheet-sets/line-sheet-set-row.type";
import {
  BuyerOrderSheetRow,
  BuyerOrderSheetSetRow,
} from "@/features/order-status/order-sheet-set-row.type";
import useLineSheetSetGrid from "@/features/order-sheet-sets/hooks/use-line-sheet-set-grid";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import HelpTooltip from "@/shared/components/help-tooltip";
import { query } from "@zag-js/dom-query";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";
import { PersistedOrderSheetSetListQuery } from "@/features/order-status/agency-order-sheet-set-list-page";
import CustomIcon from "../../../public/icons-v1_1";
import { highlightPartialText } from "@/utils/highlightSearch";
import useGetCompanies from "@/features/order-sheet-sets/hooks/useGetCompanies";
import { Fragment, useMemo } from "react";

export default function BuyerOrderSheetSetGrid() {
  const identity = useIdentity();
  const { error: showError } = useAppToasts();
  const {
    rows,
    mutateRows,
    discloseTooltip,
    hover,
    toggleCollapseIfRoot,
    toggleCheck,
  } = useBuyerOrderSheetSetRows();
  const {
    t,
    tTitle,
    tLocalDateString,
    tLocalDateTimeString,
    tLocalDateTimeString2,
    tCurrencyString,
    tSentence,
    isKor,
  } = useI18nHelper();

  const {
    navigateToOrderSheetSetPage,
    navigateToBuyerOrderSheetSetPage,
    navigate,
    navigateToOrderHistoryDetailPage,
  } = useRouterHelper();

  const {
    disclosureUploadOrderSheetModal,
    disclosureOrderOrderSheetSetModal,
    fields,
    actions,
    extraContents,
  } = useLineSheetSetGrid({
    rows,
    discloseTooltip,
    mutateRows,
  });

  const [query, setQuery] =
    useTypedSearchParams<PersistedOrderSheetSetListQuery>();

  const brandList = useGetCompanies(["BRAND"]);

  const selectedBrands = useMemo(() => {
    return (
      query["madeByCompany.id__in"]
        ?.map(
          (company) => brandList?.find((brand) => brand.id === +company)?.name
        )
        .filter((brand): brand is string => !!brand) ?? []
    );
  }, [brandList, query]);

  return (
    <>
      <AppGrid
        metas={[
          {
            rowType: "OrderSheetSet",
            gap: "4px",
            columns: [
              // { name: tTitle("types"), width: "64px" },
              { name: tTitle("order_id"), width: "80px" },
              // { name: tTitle("brand"), width: "120px" },
              { name: tTitle("supplier"), width: "80px" },
              {
                name: tTitle("order_history:order_info"),
                width: "minmax(224px, 1fr)",
              },
              {
                name: tTitle("ordered_amount.abbr"),
                width: "144px",
                textAlign: "end",
                marginRight: "24px",
                value: (
                  <Flex
                    gap={"4px"}
                    alignItems="center"
                    justifyContent={"flex-end"}
                  >
                    <Text>{tTitle("ordered_amount.abbr")}</Text>
                    <HelpTooltip
                      text={tSentence("order_history:ordered_tooltip")}
                    />
                  </Flex>
                ),
              },
              // {
              //   name: tTitle("confirmed_amount.abbr"),
              //   width: "144px",
              //   textAlign: "end",
              //   marginRight: "24px",
              // },
              // { name: tTitle("ordered_on"), width: "148px" },
              {
                name: tTitle("status"),
                width: "180px",
                value: (
                  <Flex gap={"4px"} alignItems="center">
                    <Text>{tTitle("status")}</Text>
                    <HelpTooltip
                      placement={"left"}
                      text={
                        <>
                          <Text textStyle={"body-sm-bold"}>
                            {tSentence("order_history:status_tooltip.title")}
                          </Text>
                          <UnorderedList>
                            <ListItem>
                              {tSentence(
                                "order_history:status_tooltip.order_submitted"
                              )}
                            </ListItem>
                            <ListItem>
                              {tSentence(
                                "order_history:status_tooltip.order_consulting"
                              )}
                            </ListItem>
                            <ListItem>
                              {tSentence(
                                "order_history:status_tooltip.buyer_modification_required"
                              )}
                            </ListItem>
                            <ListItem>
                              {tSentence(
                                "order_history:status_tooltip.resubmit_completed"
                              )}
                            </ListItem>
                            <ListItem>
                              {tSentence(
                                "order_history:status_tooltip.buyer_confirm_required"
                              )}
                            </ListItem>
                            <ListItem>
                              {tSentence(
                                "order_history:status_tooltip.order_confirmed"
                              )}
                            </ListItem>
                            <ListItem>
                              {tSentence(
                                "order_history:status_tooltip.transportation_ing"
                              )}
                            </ListItem>
                            <ListItem>
                              {tSentence(
                                "order_history:status_tooltip.transportation_completed"
                              )}
                            </ListItem>
                            <ListItem>
                              {tSentence(
                                "order_history:status_tooltip.completed"
                              )}
                            </ListItem>
                            <ListItem>
                              {tSentence(
                                "order_history:status_tooltip.order_cancelled"
                              )}
                            </ListItem>
                          </UnorderedList>
                        </>
                      }
                    />
                  </Flex>
                ),
              },
              {
                name: tTitle("remittance_status.remittance_required"),
                width: "180px",
                value: (
                  <Flex gap={"4px"} alignItems="center">
                    <Text>
                      {tTitle("remittance_status.remittance_required")}
                    </Text>
                    <HelpTooltip
                      placement={"left"}
                      text={
                        <>
                          <Text textStyle={"body-sm-bold"}>
                            {tSentence(
                              "order_history:remittance_required_tooltip.title"
                            )}
                          </Text>
                          <Text>
                            {tSentence(
                              "order_history:remittance_required_tooltip.description"
                            )}
                          </Text>
                          <UnorderedList>
                            <ListItem>
                              {tSentence(
                                "order_history:remittance_required_tooltip.payment_for_goods"
                              )}
                            </ListItem>
                            <ListItem>
                              {tSentence(
                                "order_history:remittance_required_tooltip.request_for_funds"
                              )}
                            </ListItem>
                            <ListItem>
                              {tSentence(
                                "order_history:remittance_required_tooltip.service_fee"
                              )}
                            </ListItem>
                          </UnorderedList>
                        </>
                      }
                    />
                  </Flex>
                ),
              },
            ],
            fields: [
              // fields.lineSheetSetType,
              {
                name: "order_id",
                chakraProps: {
                  fontWeight: "400",
                  fontSize: "13px",
                  color: "#444444",
                },
                value: (r) => {
                  const orderSheetSetId = (r as BuyerOrderSheetSetRow).id;
                  return `P${orderSheetSetId}`;
                },
              },
              // {
              //   name: "brand",
              //   chakraProps: { fontWeight: "700" },
              //   value: (r) => {
              //     return (
              //       <Text textStyle={"body-sm-regular"} color={"#444444"}>
              //         {Object.keys(
              //           (r as BuyerOrderSheetSetRow).statisticByBrand ?? {}
              //         ).join(", ")}
              //       </Text>
              //     );
              //   },
              // },
              {
                name: "supplier",
                chakraProps: {
                  fontWeight: "700",
                  fontSize: "13px",
                  color: "#444444",
                },
                value: (r) => {
                  return (r as BuyerOrderSheetSetRow).issuedByBoutiqueName;
                },
              },
              {
                name: "order_info",
                chakraProps: { fontWeight: "700" },
                value: (r) => {
                  const LineSheetSetId = (r as BuyerOrderSheetSetRow).id;
                  const newLssId = (r as BuyerOrderSheetSetRow).lineSheetSetId;
                  const title = (r as BuyerOrderSheetSetRow).name;
                  const orderedAt = (r as BuyerOrderSheetSetRow).submittedAt;
                  const brandList = Object.keys(
                    (r as BuyerOrderSheetSetRow).statisticByBrand ?? {}
                  ).sort((prev, next) => prev.localeCompare(next));
                  const titleTxt = `#${newLssId} ${title}`;

                  return (
                    <Flex flexDirection={"column"} gap={"4px"}>
                      <Text textStyle={"caption-regular"} color={"#6F6F6C"}>
                        {orderedAt
                          ? `${tLocalDateTimeString2(orderedAt)} ${t("submit")}`
                          : ""}
                      </Text>
                      <Text textStyle={"body-sm-bold"} noOfLines={1}>
                        {highlightPartialText(titleTxt, [
                          query.query,
                          ...selectedBrands,
                        ])}
                      </Text>
                      <Flex alignItems={"center"}>
                        {brandList.slice(0, 6).map((brand, index) => (
                          <Fragment key={index}>
                            {index !== 0 && (
                              <CustomIcon
                                src={"dot"}
                                width={16}
                                height={16}
                                color={"#D4D4D1"}
                              />
                            )}
                            <Text
                              key={brand}
                              textStyle={"caption-medium"}
                              color={"#6F6F6C"}
                              whiteSpace={"nowrap"}
                            >
                              {highlightPartialText(brand, [
                                query.query,
                                ...selectedBrands,
                              ])}
                            </Text>
                          </Fragment>
                        ))}
                        {brandList.length > 6 && (
                          <Text
                            textStyle={"caption-regular"}
                            marginLeft={"4px"}
                          >
                            {isKor
                              ? `외 ${brandList.length - 6}개`
                              : `${brandList.length - 6} more`}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  );
                },
              },
              {
                name: "OrderedAmount",
                chakraProps: { fontWeight: "700" },
                value: (r) => {
                  const orderedAmount = (r as BuyerOrderSheetSetRow)
                    .orderedAmount;
                  const orderedAmountWithoutCondition = (
                    r as BuyerOrderSheetSetRow
                  ).orderedAmountWithoutCondition;
                  const orderedConditionType = (r as BuyerOrderSheetSetRow)
                    .orderedConditionType;
                  let conditionStr = "";
                  if (orderedConditionType === "DISCOUNT") {
                    conditionStr = "R- ";
                  } else if (orderedConditionType === "MARKUP") {
                    conditionStr = "C+ ";
                  } else if (orderedConditionType === "MIX") {
                    conditionStr = "R/C ";
                  }
                  return (
                    <Flex
                      flexDirection={"column"}
                      fontSize={"12px"}
                      alignItems={"flex-end"}
                      marginRight={"24px"}
                    >
                      {/*<Text>*/}
                      {/*  {tCurrencyString(orderedAmountWithoutCondition, {*/}
                      {/*    types: "SYMBOL",*/}
                      {/*    code: (r as BuyerOrderSheetSetRow).currency,*/}
                      {/*  })}*/}
                      {/*</Text>*/}
                      <Text>
                        {/*({conditionStr}*/}
                        {/*{tCurrencyString(orderedAmount, {*/}
                        {/*  types: "SYMBOL",*/}
                        {/*  code: (r as BuyerOrderSheetSetRow).currency,*/}
                        {/*})}*/}
                        {/*)*/}
                        {tCurrencyString(orderedAmount, {
                          type: "SYMBOL",
                          code: (r as BuyerOrderSheetSetRow).currency,
                        })}
                      </Text>
                    </Flex>
                  );
                },
              },
              // {
              //   name: "ConfirmedAmount",
              //   chakraProps: { fontWeight: "700" },
              //   value: (r) => {
              //     const value = (r as BuyerOrderSheetSetRow).confirmedAmount;
              //     return (
              //       <Flex
              //         flexDirection={"column"}
              //         fontSize={"12px"}
              //         alignItems={"flex-end"}
              //         marginRight={"24px"}
              //       >
              //         <Text>
              //           {tCurrencyString(value, {
              //             types: "SYMBOL",
              //             code: (r as BuyerOrderSheetSetRow).currency,
              //           })}
              //         </Text>
              //       </Flex>
              //     );
              //   },
              // },
              // {
              //   name: "orderedAt",
              //   chakraProps: { fontWeight: "700" },
              //   value: (r) => {
              //     const value = (r as BuyerOrderSheetSetRow).submittedAt;
              //     return value ? tLocalDateTimeString2(value) : "";
              //   },
              // },
              fields.withOrderSheetStatus(true, true),
              fields.withRemittanceRequiredStatus(),
            ],
            // actions: [
            //   // actions.orderOrderSheetSet,
            //   actions.withDownloadUnoFile<BuyerOrderSheetSetRow>(
            //     (row) => row.lineSheetSet.compressedImportedFile
            //   ),
            //   actions.withDownloadUnoForm<BuyerOrderSheetSetRow>(
            //     (row) => row.lineSheetSet.compressedUnoFormFile
            //   ),
            // ],
            indicator: (row) => {
              return (
                <GridRowIndicator
                  type={"VIEW"}
                  isHovered={row.isHovered}
                  isCollapsed={row.isCollapsed}
                />
              );
            },
            // onClick: (row) => {
            //   const orderSheetSetRow = row as BuyerOrderSheetSetRow;
            //   navigateToOrderSheetSetPage(
            //     orderSheetSetRow.lineSheetSetId,
            //     row.id
            //   );
            // },
            onClick: (row) => {
              const orderSheetSetRow = row as BuyerOrderSheetSetRow;
              // navigate(
              //   `/order-sheet-sets/order-history?order_sheet_set_id=${orderSheetSetRow.id}&status=${orderSheetSetRow.status}&line_sheet_set.type__eq=${orderSheetSetRow.types}`
              // );
              navigateToOrderHistoryDetailPage(
                orderSheetSetRow.id,
                orderSheetSetRow.status,
                orderSheetSetRow.type
              );
              // if (orderSheetSetRow.lineSheetSet.status === "PARSING_COMPLETED") {
              //   navigateToBuyerOrderSheetSetPage(
              //     orderSheetSetRow.id,
              //     orderSheetSetRow.lineSheetSetId,
              //   );
              // } else {
              //   navigateToOrderSheetSetPage(
              //     orderSheetSetRow.lineSheetSetId,
              //     row.id
              //   );
              // }
            },
          },
          {
            rowType: "LineSheet",
            columns: [
              { name: "type", width: "232px" },
              { name: "name", width: "1fr" },
              { name: "status", width: "144px" },
            ],
            fields: [
              fields.withRowType("line_sheet"),
              {
                name: "name",
                chakraProps: {
                  fontWeight: 700,
                },
                value: (r) => {
                  return (r as BuyerLineSheetRow).name;
                },
              },
            ],
            // onClick: (row) => {
            // },
            actions: [
              actions.writeDraft,
              actions.writeFallbackDraft,
              actions.viewLineSheet,
              actions.withDownloadUnoFile<BuyerLineSheetRow>((row) => row.file),
            ],
            tooltip: (_row) => {
              const row = _row as BuyerLineSheetRow;
              if (row.isWritable) {
                return (
                  <OrderSheetRowTooltipLabel isFallback={row.isFallback} />
                );
              }
            },
          },
          {
            rowType: "Attachment",
            columns: [
              { name: "type", width: "232px" },
              { name: "name", width: "1fr" },
              { name: "status", width: "144px" },
            ],
            fields: [
              fields.storedObjectField,
              {
                name: "name",
                value: (row) => {
                  const attachmentRow = row as BuyerAttachmentRow;
                  return attachmentRow.name;
                },
              },
            ],
            actions: [
              actions.withDownloadUnoFile<BuyerAttachmentRow>((row) => row),
            ],
          },
          {
            rowType: "OrderSheet",
            columns: [
              { name: "type", width: "152px" },
              { name: "name", width: "1fr" },
              { name: "orderedAmount", width: "240px" },
              { name: "boutiqueName ", width: "144px" },
              { name: "status", width: "144px" },
            ],
            fields: [
              fields.withRowType("order_sheet"),
              {
                name: "name",
                chakraProps: {
                  fontWeight: 700,
                },
                value: (r) => {
                  const orderSheetRow = r as BuyerOrderSheetRow;
                  const lineSheetRow = findParentGridRow(
                    r,
                    rows
                  ) as BuyerLineSheetRow;
                  return lineSheetRow.name;
                },
              },
              {
                name: "OrderedAmount",
                value: (r) => {
                  const orderSheetRow = r as BuyerOrderSheetRow;
                  const orderedAmount = orderSheetRow.orderedAmount;
                  const orderedAmountWithoutCondition =
                    orderSheetRow.orderedAmountWithoutCondition;
                  const orderedConditionType =
                    orderSheetRow.orderedConditionType;
                  let conditionStr = "";
                  if (orderedConditionType === "DISCOUNT") {
                    conditionStr = "R- ";
                  } else if (orderedConditionType === "MARKUP") {
                    conditionStr = "C+ ";
                  } else if (orderedConditionType === "MIX") {
                    conditionStr = "R/C ";
                  }
                  return (
                    <Text textAlign={"end"} marginEnd={"80px"}>
                      {tCurrencyString(orderedAmountWithoutCondition, {
                        type: "SYMBOL",
                        code: orderSheetRow.currency,
                      })}{" "}
                      ({conditionStr}
                      {tCurrencyString(orderedAmount, {
                        type: "SYMBOL",
                        code: orderSheetRow.currency,
                      })}
                      )
                    </Text>
                  );
                },
              },
              {
                name: "boutiqueName",
                value: (r) => {
                  const orderSheetRow = r as BuyerOrderSheetRow;
                  return orderSheetRow.toBeSubmitted?.name || "";
                },
              },
              fields.withOrderSheetStatus(true),
            ],
            actions: [
              actions.editOrViewOrderSheet,
              actions.replaceOrUploadUploadedFile,
              actions.deleteUploadedFile,
              actions.withDownloadUnoFile<BuyerOrderSheetRow>(
                (row) => row.uploadedFile
              ),
            ],
            extraContents: extraContents.orderSheetExtraContents,
          },
        ]}
        rows={rows}
        onHover={(path, row, isHovered) => {
          hover(row, isHovered);
        }}
        onClick={(path, row) => {
          //toggleCollapseIfRoot(row);
        }}
        onCheck={(path, row, isChecked) => {
          toggleCheck(row);
        }}
        isOrderHistory={true}
        onReset={() =>
          setQuery({
            "lineSheetSet.type__eq": query["lineSheetSet.type__eq"],
            statusGroup__eq: "ALL",
            pageNumber: 1,
          })
        }
      ></AppGrid>
      {disclosureUploadOrderSheetModal.data && (
        <UploadOrderSheetsModal
          {...disclosureUploadOrderSheetModal.data}
          isOpen={disclosureUploadOrderSheetModal.isOpen}
          onClose={disclosureUploadOrderSheetModal.onClose}
        />
      )}
      {disclosureOrderOrderSheetSetModal.data && (
        <OrderOrderSheetSetModal
          {...disclosureOrderOrderSheetSetModal.data}
          isOpen={disclosureOrderOrderSheetSetModal.isOpen}
          onClose={disclosureOrderOrderSheetSetModal.onClose}
        />
      )}
    </>
  );
}
